import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils';
import { MODULES, getModuleByPath } from '@/shared/constants/modules';
import { usePermissions } from '@/shared/contexts/PermissionContext';
import {
  LayoutDashboard,
  Package,
  Route as RouteIcon,
  Users,
  Truck,
  Warehouse,
  Building2,
  CreditCard,
  BarChart3,
  Settings,
  Bell,
  FileText,
  UserCog,
  MessageSquare,
  Send,
  FileCode,
  Activity,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Home,
  ScanLine,
  Box,
  ClipboardList,
  Layers,
  PackageCheck,
  DollarSign,
  BookOpen,
  Receipt,
  Landmark,
  Calculator,
  PiggyBank,
  FileSpreadsheet,
  ShoppingCart,
  Upload,
  Network,
  History,
  Thermometer,
  Radio,
  AlertTriangle,
  Link as LinkIcon2,
  Navigation,
  Play,
  Printer,
  Smartphone,
  MapPin,
  Cpu,
  Shield,
  FileCheck,
  FolderOpen,
  Zap,
  Database,
  Key,
  Globe,
  HelpCircle,
  Book,
  Wrench,
  Fuel,
  Target,
  Store,
  Share2,
  PenSquare,
  CalendarClock,
  TrendingUp,
  Mail,
  Megaphone,
  FileBarChart,
  GitBranch,
  Presentation } from
'lucide-react';

// EzParcel Navigation - using translation keys
const ezparcelNav = [
{ nameKey: 'sidebar.ezparcel.dashboard', href: '/ezparcel/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.ezparcel.shipments', href: '/ezparcel/shipments', icon: Package },
{ nameKey: 'sidebar.ezparcel.routesDispatch', href: '/ezparcel/routes', icon: RouteIcon },
{ nameKey: 'sidebar.ezparcel.warehouses', href: '/ezparcel/warehouses', icon: Warehouse },
{ nameKey: 'sidebar.ezparcel.customers', href: '/ezparcel/customers', icon: Building2 },
{ nameKey: 'sidebar.ezparcel.billing', href: '/ezparcel/billing', icon: CreditCard },
{ nameKey: 'sidebar.ezparcel.reports', href: '/ezparcel/reports', icon: BarChart3 },
{ nameKey: 'sidebar.ezparcel.documents', href: '/ezparcel/documents', icon: FileText },
{ nameKey: 'sidebar.ezparcel.notifications', href: '/ezparcel/notifications', icon: Bell }];


// Fleet Management Navigation (embedded in EzParcel)
import { Cog, CircleDot, Route as RouteIconLucide } from 'lucide-react';
const fleetManagementNav = [
{ nameKey: 'sidebar.fleet.dashboard', href: '/ezparcel/fleet', icon: LayoutDashboard },
{ nameKey: 'sidebar.fleet.vehicleRegistry', href: '/ezparcel/fleet/vehicles', icon: Truck },
{ nameKey: 'sidebar.fleet.driverManagement', href: '/ezparcel/fleet/drivers', icon: Users },
{ nameKey: 'sidebar.fleet.liveTracking', href: '/ezparcel/fleet/tracking', icon: Navigation },
{ nameKey: 'sidebar.fleet.routeOptimization', href: '/ezparcel/fleet/route-optimization', icon: RouteIconLucide },
{ nameKey: 'sidebar.fleet.telematics', href: '/ezparcel/fleet/telematics', icon: Cpu },
{ nameKey: 'sidebar.fleet.vehicleParts', href: '/ezparcel/fleet/parts', icon: Cog },
{ nameKey: 'sidebar.fleet.tires', href: '/ezparcel/fleet/tires', icon: CircleDot },
{ nameKey: 'sidebar.fleet.procurement', href: '/ezparcel/fleet/procurement', icon: ShoppingCart },
{ nameKey: 'sidebar.fleet.fuelLogs', href: '/ezparcel/fleet/fuel', icon: Fuel },
{ nameKey: 'sidebar.fleet.workOrders', href: '/ezparcel/fleet/maintenance', icon: Wrench },
{ nameKey: 'sidebar.fleet.analytics', href: '/ezparcel/fleet/analytics', icon: BarChart3 },
{ nameKey: 'sidebar.fleet.driverMobile', href: '/ezparcel/fleet/driver-mobile', icon: Smartphone },
{ nameKey: 'sidebar.fleet.alerts', href: '/ezparcel/fleet/alerts', icon: AlertTriangle }];


const ezparcelCommunicationsNav = [
{ nameKey: 'sidebar.communications.integrations', href: '/ezparcel/integrations', icon: MessageSquare },
{ nameKey: 'sidebar.communications.rules', href: '/ezparcel/communications/rules', icon: Send },
{ nameKey: 'sidebar.communications.templates', href: '/ezparcel/communications/templates', icon: FileCode },
{ nameKey: 'sidebar.communications.messageLogs', href: '/ezparcel/communications/logs', icon: Activity }];


// Cold Chain Navigation (embedded in EzParcel)
const coldChainNav = [
{ nameKey: 'sidebar.coldChain.dashboard', href: '/ezparcel/coldchain/dashboard', icon: Thermometer },
{ nameKey: 'sidebar.coldChain.sensors', href: '/ezparcel/coldchain/sensors', icon: Radio },
{ nameKey: 'sidebar.coldChain.excursions', href: '/ezparcel/coldchain/excursions', icon: AlertTriangle },
{ nameKey: 'sidebar.coldChain.rules', href: '/ezparcel/coldchain/rules', icon: Settings },
{ nameKey: 'sidebar.coldChain.binding', href: '/ezparcel/coldchain/binding', icon: LinkIcon2 }];


// Trails Navigation (embedded in EzParcel)
const trailsNav = [
{ nameKey: 'sidebar.trails.dashboard', href: '/ezparcel/trails/dashboard', icon: Navigation },
{ nameKey: 'sidebar.trails.routeReplay', href: '/ezparcel/trails/replay', icon: Play }];


const ezparcelAdminNav = [
{ nameKey: 'sidebar.ezparcelAdmin.admin', href: '/ezparcel/admin', icon: UserCog },
{ nameKey: 'sidebar.ezparcelAdmin.settings', href: '/ezparcel/settings', icon: Settings }];


// WMS Navigation
const wmsNav = [
{ nameKey: 'sidebar.wms.dashboard', href: '/wms/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.wms.inventory', href: '/wms/inventory', icon: Package },
{ nameKey: 'sidebar.wms.adjustments', href: '/wms/adjustments', icon: ClipboardList },
{ nameKey: 'sidebar.wms.barcodeScanner', href: '/wms/scanner', icon: ScanLine },
{ nameKey: 'sidebar.wms.pharmaScan', href: '/wms/pharmascan', icon: ScanLine },
{ nameKey: 'sidebar.wms.products', href: '/wms/products', icon: Box },
{ nameKey: 'sidebar.wms.receiving', href: '/wms/receiving', icon: Truck },
{ nameKey: 'sidebar.wms.putaway', href: '/wms/putaway', icon: PackageCheck },
{ nameKey: 'sidebar.wms.picking', href: '/wms/picking', icon: ClipboardList },
{ nameKey: 'sidebar.wms.packing', href: '/wms/packing', icon: Box },
{ nameKey: 'sidebar.wms.pallets', href: '/wms/pallets', icon: Layers },
{ nameKey: 'sidebar.wms.expedition', href: '/wms/expedition', icon: Truck },
{ nameKey: 'sidebar.wms.transfers', href: '/wms/transfers', icon: Building2 },
{ nameKey: 'sidebar.wms.rushOrders', href: '/wms/rush-orders', icon: AlertTriangle },
{ nameKey: 'sidebar.wms.returns', href: '/wms/returns', icon: Truck },
{ nameKey: 'sidebar.wms.config', href: '/wms/config', icon: Warehouse },
{ nameKey: 'sidebar.wms.reports', href: '/wms/reports', icon: LayoutDashboard }];


// WMS Device Management Sub-navigation
const wmsDevicesNav = [
{ nameKey: 'sidebar.wmsDevices.allDevices', href: '/wms/devices', icon: Cpu },
{ nameKey: 'sidebar.wmsDevices.scanners', href: '/wms/devices/scanners', icon: ScanLine },
{ nameKey: 'sidebar.wmsDevices.printers', href: '/wms/devices/printers', icon: Printer },
{ nameKey: 'sidebar.wmsDevices.mobileDevices', href: '/wms/devices/mobile', icon: Smartphone },
{ nameKey: 'sidebar.wmsDevices.gpsTrackers', href: '/wms/devices/gps', icon: MapPin },
{ nameKey: 'sidebar.wmsDevices.tempSensors', href: '/wms/devices/sensors', icon: Thermometer }];


// WMS Monitoring Sub-navigation
const wmsMonitoringNav = [
{ nameKey: 'sidebar.wmsMonitoring.gpsTracking', href: '/wms/gps-tracking', icon: Navigation },
{ nameKey: 'sidebar.wmsMonitoring.temperatureAlerts', href: '/wms/temperature-alerts', icon: Thermometer },
{ nameKey: 'sidebar.wmsMonitoring.geofencing', href: '/wms/geofencing', icon: MapPin }];


// WMS Trail Visualization Sub-navigation
const wmsTrailsNav = [
{ nameKey: 'sidebar.wmsTrails.trailDashboard', href: '/wms/trails', icon: Activity },
{ nameKey: 'sidebar.wmsTrails.auditTrail', href: '/wms/trails/audit', icon: Shield },
{ nameKey: 'sidebar.wmsTrails.operationalTrail', href: '/wms/trails/operational', icon: Play },
{ nameKey: 'sidebar.wmsTrails.scanTrail', href: '/wms/trails/scan', icon: ScanLine },
{ nameKey: 'sidebar.wmsTrails.proofTrail', href: '/wms/trails/proof', icon: FileCheck }];


// WMS Print Workflow Sub-navigation
const wmsPrintNav = [
{ nameKey: 'sidebar.wmsPrint.printWorkflow', href: '/wms/print', icon: Printer }];


// WMS Document & Route Sub-navigation
const wmsDocumentNav = [
{ nameKey: 'sidebar.wmsDocuments.documentVault', href: '/wms/documents', icon: FolderOpen },
{ nameKey: 'sidebar.wmsDocuments.routeOptimization', href: '/wms/route-optimization', icon: Zap }];


// Landing Page Setup Navigation
import { Layout, Palette, History as HistoryIcon, Languages } from 'lucide-react';
const landingSetupNav = [
{ nameKey: 'sidebar.landing.dashboard', href: '/admin/landing', icon: Layout },
{ nameKey: 'sidebar.landing.sections', href: '/admin/landing/sections', icon: Layers },
{ nameKey: 'sidebar.landing.branding', href: '/admin/landing/branding', icon: Palette },
{ nameKey: 'sidebar.landing.translations', href: '/admin/landing/translations', icon: Languages },
{ nameKey: 'sidebar.landing.versions', href: '/admin/landing/versions', icon: HistoryIcon }];


// Administration Navigation
const adminNav = [
{ nameKey: 'sidebar.admin.helpCenter', href: '/help', icon: Book },
{ nameKey: 'sidebar.admin.accessProfiles', href: '/admin/access-profiles', icon: Shield },
{ nameKey: 'sidebar.admin.systemSettings', href: '/admin/system-settings', icon: Database },
{ nameKey: 'sidebar.admin.apiKeys', href: '/admin/api-keys', icon: Key },
{ nameKey: 'sidebar.admin.landingPage', href: '/admin/landing', icon: Globe },
{ nameKey: 'sidebar.admin.userPreferences', href: '/settings/user', icon: Globe }];


// Finance Navigation
const financeNav = [
{ nameKey: 'sidebar.finance.dashboard', href: '/finance/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.finance.generalLedger', href: '/finance/general-ledger', icon: BookOpen },
{ nameKey: 'sidebar.finance.accountsPayable', href: '/finance/accounts-payable', icon: Receipt },
{ nameKey: 'sidebar.finance.accountsReceivable', href: '/finance/accounts-receivable', icon: DollarSign },
{ nameKey: 'sidebar.finance.fixedAssets', href: '/finance/fixed-assets', icon: Landmark },
{ nameKey: 'sidebar.finance.taxManagement', href: '/finance/tax', icon: Calculator },
{ nameKey: 'sidebar.finance.banking', href: '/finance/banking', icon: PiggyBank },
{ nameKey: 'sidebar.finance.financialReports', href: '/finance/reports', icon: FileSpreadsheet },
{ nameKey: 'sidebar.finance.expenseClaims', href: '/finance/expenses', icon: CreditCard },
{ nameKey: 'sidebar.finance.budgets', href: '/finance/budgets', icon: Target },
{ nameKey: 'sidebar.finance.documentSetup', href: '/finance/document-setup', icon: Settings },
{ nameKey: 'sidebar.finance.bankFeeds', href: '/finance/bank-feeds', icon: Building2 }];


// Procurement Navigation
const procurementNav = [
{ nameKey: 'sidebar.procurement.dashboard', href: '/procurement/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.procurement.vendors', href: '/procurement/vendors', icon: Users },
{ nameKey: 'sidebar.procurement.requisitions', href: '/procurement/requisitions', icon: FileText },
{ nameKey: 'sidebar.procurement.purchaseOrders', href: '/procurement/purchase-orders', icon: FileText },
{ nameKey: 'sidebar.procurement.rfqManagement', href: '/procurement/rfq', icon: ClipboardList },
{ nameKey: 'sidebar.procurement.goodsReceipt', href: '/procurement/goods-receipt', icon: Package },
{ nameKey: 'sidebar.procurement.supplierReturns', href: '/procurement/supplier-returns', icon: Truck },
{ nameKey: 'sidebar.procurement.vendorPayments', href: '/procurement/payments', icon: CreditCard },
{ nameKey: 'sidebar.procurement.reports', href: '/procurement/reports', icon: BarChart3 }];


// Ecommerce Navigation
const ecommerceNav = [
{ nameKey: 'sidebar.ecommerce.dashboard', href: '/ecommerce/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.ecommerce.builder', href: '/ecommerce/builder', icon: Palette },
{ nameKey: 'sidebar.ecommerce.stores', href: '/ecommerce/stores', icon: Store },
{ nameKey: 'sidebar.ecommerce.products', href: '/ecommerce/products', icon: Package },
{ nameKey: 'sidebar.ecommerce.orders', href: '/ecommerce/orders', icon: ShoppingCart }];


// CRM Navigation
const crmNav = [
{ nameKey: 'sidebar.crm.dashboard', href: '/crm/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.crm.leads', href: '/crm/leads', icon: Users },
{ nameKey: 'sidebar.crm.prospectDiscovery', href: '/crm/prospect-discovery', icon: Target },
{ nameKey: 'sidebar.crm.pipeline', href: '/crm/pipeline', icon: BarChart3 },
{ nameKey: 'sidebar.crm.opportunities', href: '/crm/opportunities', icon: Package },
{ nameKey: 'sidebar.crm.quotes', href: '/crm/quotes', icon: FileText },
{ nameKey: 'sidebar.crm.campaignIntelligence', href: '/crm/campaign-intelligence', icon: TrendingUp },
{ nameKey: 'sidebar.crm.marketingHub', href: '/crm/marketing-hub', icon: Megaphone },
{ nameKey: 'sidebar.crm.accounts', href: '/crm/accounts', icon: Building2 },
{ name: 'Orchestration', href: '/crm/orchestration', icon: GitBranch },
{ nameKey: 'sidebar.crm.portalManager', href: '/crm/portal-manager', icon: Globe },
{ nameKey: 'sidebar.crm.publishingConsole', href: '/crm/publishing-console', icon: Send },
{ nameKey: 'sidebar.crm.adConnectors', href: '/crm/ad-connectors', icon: Network },
{ name: 'The Presentation HUB', href: '/crm/presentation-hub', icon: Presentation },
{ nameKey: 'sidebar.crm.dropship', href: '/crm/dropship', icon: Truck },
{ nameKey: 'sidebar.crm.consignment', href: '/crm/consignment', icon: Package },
{ nameKey: 'sidebar.crm.activities', href: '/crm/activities', icon: Bell },
{ nameKey: 'sidebar.crm.reports', href: '/crm/reports', icon: BarChart3 }];


// Human Resources Navigation
const hrNavigation = [
{ name: 'Dashboard', href: '/hr/dashboard', icon: LayoutDashboard },
{ name: 'Employees', href: '/hr/employees', icon: Users },
{ name: 'Time & Attendance', href: '/hr/time-attendance', icon: CalendarClock },
{ name: 'Leave Management', href: '/hr/leave', icon: CalendarIcon },
{ name: 'Payroll', href: '/hr/payroll', icon: DollarSign },
{ name: 'Pay Components', href: '/hr/pay-components', icon: Receipt },
{ name: 'Shifts', href: '/hr/shifts', icon: CalendarClock },
{ name: 'Positions', href: '/hr/positions', icon: Building2 }];


// Accounting Navigation
const accountingNavigation = [
{ name: 'Dashboard', href: '/accounting/dashboard', icon: LayoutDashboard },
{ name: 'Chart of Accounts', href: '/accounting/chart-of-accounts', icon: BookOpen },
{ name: 'Journal Entries', href: '/accounting/journals', icon: FileText },
{ name: 'Event Inbox', href: '/accounting/events', icon: Activity },
{ name: 'Period Close', href: '/accounting/periods', icon: CalendarClock },
{ name: 'Statutory Exports', href: '/accounting/exports', icon: FileSpreadsheet },
{ name: 'Posting Rules', href: '/accounting/posting-rules', icon: Settings }];

// Fiscal Navigation
const fiscalNavigation = [
{ name: 'Dashboard', href: '/fiscal/dashboard', icon: LayoutDashboard },
{ name: 'Fiscal Documents', href: '/fiscal/documents', icon: FileCheck },
{ name: 'Apuração', href: '/fiscal/apuracao', icon: Calculator },
{ name: 'SPED / EFD', href: '/fiscal/sped', icon: FileSpreadsheet },
{ name: 'Fiscal Matrix', href: '/fiscal/matrix', icon: Database },
{ name: 'Fiscal Events', href: '/fiscal/events', icon: Activity },
{ name: 'Audit Evidence', href: '/fiscal/audit', icon: Shield }];


// Communications Navigation
const communicationsNav = [
{ nameKey: 'sidebar.comms.dashboard', href: '/communications/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.comms.telegram', href: '/communications/telegram', icon: Send },
{ nameKey: 'sidebar.comms.whatsApp', href: '/communications/whatsapp', icon: MessageSquare },
{ nameKey: 'sidebar.comms.sms', href: '/communications/sms', icon: CreditCard },
{ nameKey: 'sidebar.comms.email', href: '/communications/email', icon: FileText },
{ nameKey: 'sidebar.comms.templates', href: '/communications/templates', icon: FileCode },
{ nameKey: 'sidebar.comms.rules', href: '/communications/rules', icon: Bell },
{ nameKey: 'sidebar.comms.logs', href: '/communications/logs', icon: Activity }];


// OIG Module Navigation
const oigNavigation = [
{ nameKey: 'sidebar.oig.dashboard', href: '/oig/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.oig.asnReceipts', href: '/oig/asn', icon: Package },
{ nameKey: 'sidebar.oig.nfe', href: '/oig/nfe', icon: FileText },
{ nameKey: 'sidebar.oig.nfeConfig', href: '/oig/nfe/config', icon: Settings },
{ nameKey: 'sidebar.oig.nfeUpload', href: '/oig/nfe/upload', icon: Upload },
{ nameKey: 'sidebar.oig.cte', href: '/oig/cte', icon: Truck },
{ nameKey: 'sidebar.oig.ediPartners', href: '/oig/edi', icon: Network },
{ nameKey: 'sidebar.oig.csvImport', href: '/oig/csv', icon: FileSpreadsheet },
{ nameKey: 'sidebar.oig.history', href: '/oig/history', icon: History }];


// Calendar Module Navigation
import { Calendar as CalendarIcon, Cloud, Zap as ZapIcon, CalendarDays, Clock, CheckSquare, KanbanSquare } from 'lucide-react';
const calendarNavigation = [
{ nameKey: 'sidebar.calendar.calendar', href: '/calendar', icon: CalendarIcon },
{ nameKey: 'sidebar.calendar.eventRules', href: '/calendar/rules', icon: ZapIcon },
{ nameKey: 'sidebar.calendar.holidays', href: '/calendar/holidays', icon: CalendarDays },
{ nameKey: 'sidebar.calendar.workingHours', href: '/calendar/working-hours', icon: Clock },
{ nameKey: 'sidebar.calendar.externalSync', href: '/calendar/sync', icon: Cloud }];


// EzTask Module Navigation (Kanban Project Management)
const eztaskNavigation = [
{ nameKey: 'sidebar.eztask.boards', href: '/eztask', icon: KanbanSquare },
{ nameKey: 'sidebar.eztask.dashboard', href: '/eztask/dashboard', icon: LayoutDashboard }];


// CNS Social Media Management Navigation
const cnsNavigation = [
{ nameKey: 'sidebar.cns.dashboard', href: '/cns/dashboard', icon: LayoutDashboard },
{ nameKey: 'sidebar.cns.contentStudio', href: '/cns/content', icon: PenSquare },
{ nameKey: 'sidebar.cns.scheduler', href: '/cns/scheduler', icon: CalendarClock },
{ nameKey: 'sidebar.cns.channels', href: '/cns/channels', icon: Share2 },
{ nameKey: 'sidebar.cns.campaigns', href: '/cns/campaigns', icon: Target },
{ nameKey: 'sidebar.cns.analytics', href: '/cns/analytics', icon: TrendingUp },
{ nameKey: 'sidebar.cns.emailMarketing', href: '/cns/email', icon: Mail },
{ nameKey: 'sidebar.cns.adsManager', href: '/cns/ads', icon: Megaphone },
{ nameKey: 'sidebar.cns.aiConfig', href: '/cns/ai', icon: Cpu },
{ nameKey: 'sidebar.cns.settings', href: '/cns/settings', icon: Settings }];


// Report Builder Navigation
const reportsNavigation = [
{ nameKey: 'sidebar.reports.myReports', href: '/reports', icon: FileBarChart },
{ nameKey: 'sidebar.reports.newReport', href: '/reports/new', icon: PenSquare },
{ nameKey: 'sidebar.reports.types', href: '/reports/types', icon: Layers }];


const NavLink = ({ item, isCollapsed, isActive, t, indented = false }) => {
  // Get translated name using the translation key
  const name = item.nameKey ? t(item.nameKey) : item.name;

  return (
    <Link
      to={item.href}
      data-testid={`nav-link-${item.href.replace(/\//g, '-')}`}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        indented && 'ml-4 pl-4',
        isActive ?
        'bg-[hsl(var(--accent))] text-white' :
        'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
      title={isCollapsed ? name : undefined}>
      
      <item.icon className={cn("flex-shrink-0", indented ? "h-4 w-4" : "h-5 w-5")} />
      {!isCollapsed && <span className={indented ? "text-sm" : ""}>{name}</span>}
    </Link>);

};

// Collapsible Section Component with Accordion behavior
const CollapsibleSection = ({
  title,
  titleKey,
  icon: Icon,
  items,
  isCollapsed,
  location,
  permissions,
  t,
  defaultExpanded = false,
  expandedSections,
  setExpandedSections,
  sectionKey
}) => {
  const { canAccessRoute } = usePermissions();

  // Filter items by permission
  const visibleItems = permissions ?
  items.filter((item) => canAccessRoute(item.href)) :
  items;

  // Get translated title
  const sectionTitle = titleKey ? t(titleKey) : title;

  // Check if any child is active
  const hasActiveChild = visibleItems.some(
    (item) => location.pathname === item.href || location.pathname.startsWith(item.href + '/')
  );

  // Use controlled expansion state
  const isExpanded = expandedSections[sectionKey] ?? (defaultExpanded || hasActiveChild);

  const toggleSection = () => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !isExpanded
    }));
  };

  // Auto-expand if a child becomes active - moved before early returns
  useEffect(() => {
    if (hasActiveChild && !expandedSections[sectionKey]) {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionKey]: true
      }));
    }
  }, [hasActiveChild, sectionKey, expandedSections, setExpandedSections]);

  // Early return after all hooks
  if (visibleItems.length === 0) return null;

  if (isCollapsed) {
    // In collapsed mode, just show the icon as a dropdown indicator
    return (
      <div className="relative group">
        <button
          className={cn(
            'flex w-full items-center justify-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            hasActiveChild ?
            'bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]' :
            'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
          title={sectionTitle}>
          
          {Icon && <Icon className="h-5 w-5" />}
        </button>
      </div>);

  }

  return (
    <div className="mt-2">
      {/* Section Header - Clickable */}
      <button
        onClick={toggleSection}
        data-testid={`section-toggle-${sectionKey}`}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors',
          hasActiveChild ?
          'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30' :
          'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}>
        
        <div className="flex items-center gap-3">
          {Icon && <Icon className="h-5 w-5" />}
          <span className="uppercase tracking-wider text-xs">{sectionTitle}</span>
        </div>
        <div className={cn(
          "transition-transform duration-200",
          isExpanded ? "rotate-0" : "-rotate-90"
        )}>
          <ChevronDown className="h-4 w-4" />
        </div>
      </button>
      
      {/* Collapsible Content */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-in-out",
          isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        )}>
        
        <div className="mt-1 space-y-0.5 border-l-2 border-border ml-4 pl-0">
          {visibleItems.map((item) =>
          <NavLink
            key={item.nameKey || item.name}
            item={item}
            isCollapsed={isCollapsed}
            isActive={location.pathname === item.href || location.pathname.startsWith(item.href + '/')}
            t={t}
            indented={true} />

          )}
        </div>
      </div>
    </div>);

};

const NavSection = ({ title, titleKey, items, isCollapsed, location, permissions, t }) => {
  const { canAccessRoute } = usePermissions();

  // Filter items by permission
  const visibleItems = permissions ?
  items.filter((item) => canAccessRoute(item.href)) :
  items;

  if (visibleItems.length === 0) return null;

  // Get translated title
  const sectionTitle = titleKey ? t(titleKey) : title;

  return (
    <>
      {!isCollapsed && sectionTitle &&
      <div className="pt-4">
          <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {sectionTitle}
          </p>
        </div>
      }
      {visibleItems.map((item) =>
      <NavLink
        key={item.nameKey || item.name}
        item={item}
        isCollapsed={isCollapsed}
        isActive={location.pathname === item.href || location.pathname.startsWith(item.href + '/')}
        t={t} />

      )}
    </>);

};

export const Sidebar = ({ isCollapsed = false }) => {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const currentModule = getModuleByPath(location.pathname);
  const isERP = location.pathname === '/' || currentModule.id === 'erp';
  const isWMS = currentModule.id === 'wms';
  const isFinance = currentModule.id === 'finance';
  const isProcurement = currentModule.id === 'procurement';
  const isCRM = currentModule.id === 'crm';
  const isCommunications = currentModule.id === 'communications';
  const isOIG = currentModule.id === 'oig';
  const isCalendar = currentModule.id === 'calendar';
  const isEzTask = currentModule.id === 'eztask';
  const isCNS = currentModule.id === 'cns';
  const isEcommerce = currentModule.id === 'ecommerce';
  const isReports = currentModule.id === 'reports';
  const isHR = currentModule.id === 'hr';
  const isAccounting = currentModule.id === 'accounting';
  const isFiscal = currentModule.id === 'fiscal';
  const isEzParcel = currentModule.id === 'ezparcel' || !isERP && !isWMS && !isFinance && !isProcurement && !isCRM && !isCommunications && !isOIG && !isCalendar && !isEzTask && !isEcommerce && !isCNS && !isReports && !isHR && !isAccounting && !isFiscal;

  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState({});

  // Determine module color
  const moduleColor = isWMS ? '#2563eb' : isFinance ? '#16a34a' : isProcurement ? '#4f46e5' : isCRM ? '#14b8a6' : isCommunications ? '#8b5cf6' : isOIG ? '#06b6d4' : isCalendar ? '#3b82f6' : isEzTask ? '#f59e0b' : isEcommerce ? '#10b981' : isCNS ? '#ec4899' : isReports ? '#3b82f6' : isHR ? '#7c3aed' : isAccounting ? '#059669' : isFiscal ? '#dc2626' : '#dc2626';
  const moduleName = isWMS ? 'WMS' : isFinance ? 'Finance' : isProcurement ? 'Procurement' : isCRM ? 'CRM' : isCommunications ? 'Communications' : isOIG ? 'OIG' : isCalendar ? 'Calendar' : isEzTask ? 'EzTask' : isEcommerce ? 'Ecommerce' : isCNS ? 'Social Media' : isReports ? 'Report Builder' : isHR ? 'Human Resources' : isAccounting ? 'Accounting' : isFiscal ? 'Fiscal Engine' : isERP ? 'EzERP' : 'EzParcel';

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r border-border bg-card transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64'
      )}>
      
      {/* Header */}
      <div className="flex h-16 items-center border-b border-border px-4">
        <Link to="/" className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg text-white font-bold text-sm"
            style={{ backgroundColor: moduleColor }}>
            
            {isWMS ? 'WM' : 'Ez'}
          </div>
          {!isCollapsed &&
          <span className="font-bold text-xl font-['Space_Grotesk'] text-foreground">
              {moduleName}
            </span>
          }
        </Link>
      </div>

      {/* Back to ERP Button (when in a module) */}
      {!isERP &&
      <div className="p-3 border-b border-border">
          <Link
          to="/"
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
            isCollapsed && 'justify-center'
          )}
          title={isCollapsed ? t('home') : undefined}>
          
            <Home className="h-4 w-4" />
            {!isCollapsed && <span>{t('home')}</span>}
          </Link>
        </div>
      }

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3 custom-scrollbar overflow-y-auto" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
        {/* ERP Dashboard Navigation */}
        {isERP &&
        <>
            <NavLink
            item={{ nameKey: 'overview', href: '/', icon: LayoutDashboard }}
            isCollapsed={isCollapsed}
            isActive={location.pathname === '/'}
            t={t} />
          
            {!isCollapsed &&
          <div className="pt-4">
                <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {t('modules.wms')}
                </p>
              </div>
          }
            {Object.values(MODULES).filter((m) => m.enabled && m.id !== 'erp').map((module) => {
            const Icon = module.icon;
            return (
              <Link
                key={module.id}
                to={module.path}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
                title={isCollapsed ? module.name : undefined}>
                
                  <Icon className="h-5 w-5 flex-shrink-0" style={{ color: module.color }} />
                  {!isCollapsed && <span>{module.name}</span>}
                </Link>);

          })}
          </>
        }

        {/* EzParcel Navigation with Collapsible Sections */}
        {isEzParcel &&
        <>
            <NavSection items={ezparcelNav} isCollapsed={isCollapsed} location={location} t={t} />
            
            <CollapsibleSection
            title="Fleet Management"
            icon={Truck}
            items={fleetManagementNav}
            isCollapsed={isCollapsed}
            location={location}
            t={t}
            sectionKey="ezparcel-fleet"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections}
            defaultExpanded={location.pathname.startsWith('/ezparcel/fleet')} />
          
            
            <CollapsibleSection
            titleKey="sidebar.trails.title"
            icon={Navigation}
            items={trailsNav}
            isCollapsed={isCollapsed}
            location={location}
            t={t}
            sectionKey="ezparcel-trails"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.coldChain.title"
            icon={Thermometer}
            items={coldChainNav}
            isCollapsed={isCollapsed}
            location={location}
            t={t}
            sectionKey="ezparcel-coldchain"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.communications.title"
            icon={MessageSquare}
            items={ezparcelCommunicationsNav}
            isCollapsed={isCollapsed}
            location={location}
            t={t}
            sectionKey="ezparcel-comms"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.admin.title"
            icon={UserCog}
            items={ezparcelAdminNav}
            isCollapsed={isCollapsed}
            location={location}
            t={t}
            sectionKey="ezparcel-admin"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
          </>
        }

        {/* WMS Navigation with Collapsible Sections */}
        {isWMS &&
        <>
            <NavSection items={wmsNav} isCollapsed={isCollapsed} location={location} permissions t={t} />
            
            <CollapsibleSection
            titleKey="sidebar.wmsDevices.title"
            icon={Cpu}
            items={wmsDevicesNav}
            isCollapsed={isCollapsed}
            location={location}
            permissions
            t={t}
            sectionKey="wms-devices"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.wmsMonitoring.title"
            icon={Activity}
            items={wmsMonitoringNav}
            isCollapsed={isCollapsed}
            location={location}
            permissions
            t={t}
            sectionKey="wms-monitoring"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.wmsTrails.title"
            icon={Shield}
            items={wmsTrailsNav}
            isCollapsed={isCollapsed}
            location={location}
            permissions
            t={t}
            sectionKey="wms-trails"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.wmsPrint.title"
            icon={Printer}
            items={wmsPrintNav}
            isCollapsed={isCollapsed}
            location={location}
            permissions
            t={t}
            sectionKey="wms-print"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.wmsDocuments.title"
            icon={FolderOpen}
            items={wmsDocumentNav}
            isCollapsed={isCollapsed}
            location={location}
            permissions
            t={t}
            sectionKey="wms-documents"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
            
            <CollapsibleSection
            titleKey="sidebar.admin.title"
            icon={Settings}
            items={adminNav}
            isCollapsed={isCollapsed}
            location={location}
            t={t}
            sectionKey="wms-admin"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections} />
          
          </>
        }

        {/* Finance Navigation */}
        {isFinance &&
        <NavSection items={financeNav} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* Procurement Navigation */}
        {isProcurement &&
        <NavSection items={procurementNav} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* Ecommerce Navigation */}
        {isEcommerce &&
        <NavSection items={ecommerceNav} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* CRM Navigation */}
        {isCRM &&
        <NavSection items={crmNav} isCollapsed={isCollapsed} location={location} permissions t={t} />
        }

        {/* Communications Navigation */}
        {isCommunications &&
        <NavSection items={communicationsNav} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* OIG Navigation */}
        {isOIG &&
        <NavSection items={oigNavigation} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* Calendar Navigation */}
        {isCalendar &&
        <NavSection items={calendarNavigation} isCollapsed={isCollapsed} location={location} t={t} />
        }
        
        {isEzTask &&
        <NavSection items={eztaskNavigation} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* CNS Social Media Navigation */}
        {isCNS &&
        <NavSection items={cnsNavigation} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* Report Builder Navigation */}
        {isReports &&
        <NavSection items={reportsNavigation} isCollapsed={isCollapsed} location={location} t={t} />
        }

        {/* Human Resources Navigation */}
        {isHR &&
        <NavSection items={hrNavigation} isCollapsed={isCollapsed} location={location} permissions t={t} />
        }

        {/* Accounting Navigation */}
        {isAccounting &&
        <NavSection items={accountingNavigation} isCollapsed={isCollapsed} location={location} permissions t={t} />
        }

        {/* Fiscal Navigation */}
        {isFiscal &&
        <NavSection items={fiscalNavigation} isCollapsed={isCollapsed} location={location} permissions t={t} />
        }
        
        {/* Landing Page Setup - Only visible in Admin context */}
        {location.pathname.startsWith('/admin/landing') &&
        <NavSection items={landingSetupNav} isCollapsed={isCollapsed} location={location} t={t} />
        }
      </nav>
    </aside>);

};
